import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getInvoices, type Invoice } from "../lib/api/billing";
import { Loader2, ArrowLeft, FileText, Download, ExternalLink, AlertCircle } from "lucide-react";
import intelligentiaLogo from "../images/intelligentia.jpg";

function formatAmount(cents: number, currency: string): string {
  return (cents / 100).toLocaleString("fr-CA", { style: "currency", currency: currency.toUpperCase() });
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString("fr-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function statusLabel(status: string | null): { text: string; className: string } {
  switch (status) {
    case "paid": return { text: "Payée", className: "bg-brand-teal/20 text-brand-teal" };
    case "open": return { text: "En attente", className: "bg-brand-warning/20 text-brand-warning" };
    case "void": return { text: "Annulée", className: "bg-brand-muted/20 text-brand-muted" };
    case "uncollectible": return { text: "Irrécupérable", className: "bg-brand-error/20 text-brand-error" };
    default: return { text: status || "—", className: "bg-brand-muted/20 text-brand-muted" };
  }
}

export function InvoicesPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getInvoices()
      .then((data) => {
        if (!cancelled) setInvoices(data);
      })
      .catch((err) => {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          if (typeof detail === "object" && detail?.code === "NO_STRIPE_CUSTOMER") {
            setError("Votre abonnement est géré par un administrateur. Aucune facture disponible.");
          } else {
            setError("Impossible de charger les factures. Veuillez réessayer.");
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-brand-surface relative">

      <nav className="relative z-10 flex items-center justify-between px-4 sm:px-8 py-4 bg-brand-bg/80 backdrop-blur-sm border-b border-brand-border">
        <div className="flex items-center gap-2.5">
          <img src={intelligentiaLogo} alt="Intelligentia Humana" className="h-6 w-6 object-contain rounded-md" />
          <span className="text-[15px] font-semibold text-brand-text tracking-wide">Intelligentia Humana</span>
        </div>
        <button
          type="button"
          onClick={() => navigate("/pricing")}
          className="flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Forfaits
        </button>
      </nav>

      <div className="relative z-10 max-w-3xl w-full mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-brand-text mb-3">Historique de facturation</h1>
          <p className="text-base text-brand-muted">Consultez vos factures passées.</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-brand-error/5 border border-brand-error rounded-[10px] text-brand-error text-sm text-center flex items-center justify-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center">
            <Loader2 className="w-8 h-8 text-brand-muted animate-spin" />
          </div>
        ) : invoices.length === 0 && !error ? (
          <div className="text-center text-brand-muted py-12">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Aucune facture pour le moment.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invoices.map((inv) => {
              const st = statusLabel(inv.status);
              return (
                <div
                  key={inv.id}
                  className="bg-brand-bg border border-brand-border rounded-card p-5 flex items-center gap-4 hover:border-brand-primary transition-colors"
                >
                  <FileText className="w-5 h-5 text-brand-muted shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-brand-text">
                        {formatAmount(inv.amount_paid, inv.currency)}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${st.className}`}>
                        {st.text}
                      </span>
                    </div>
                    <p className="text-xs text-brand-muted">
                      {formatDate(inv.created)}
                      {inv.period_start && inv.period_end && (
                        <> — Période: {formatDate(inv.period_start)} au {formatDate(inv.period_end)}</>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {inv.invoice_url && (
                      <a
                        href={inv.invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-brand-muted hover:text-brand-text transition-colors"
                        title="Voir la facture"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    {inv.invoice_pdf && (
                      <a
                        href={inv.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-brand-muted hover:text-brand-text transition-colors"
                        title="Télécharger le PDF"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
